import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Package, MessageSquare, Mail, Phone, ArrowUpRight } from "lucide-react";
import { Logo } from "../components/Logo";
import { api } from "../lib/api";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [queries, setQueries] = useState([]);
  const [stats, setStats] = useState({ orders: 0, queries: 0, new_orders: 0 });
  const [loading, setLoading] = useState(true);
  const admin = JSON.parse(localStorage.getItem("arkaari_admin") || "{}");

  useEffect(() => {
    const load = async () => {
      try {
        const [o, q, s] = await Promise.all([
          api.get("/custom-orders"),
          api.get("/queries"),
          api.get("/admin/stats"),
        ]);
        setOrders(o.data);
        setQueries(q.data);
        setStats(s.data);
      } catch (err) {
        if (err.response?.status === 401) logout();
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const logout = () => {
    localStorage.removeItem("arkaari_token");
    localStorage.removeItem("arkaari_admin");
    navigate("/admin/login");
  };

  const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

  return (
    <div className="min-h-screen bg-[#F5F2EA]">
      <header className="bg-[#121212] text-[#F5F2EA] sticky top-0 z-40">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Logo color="#D9BE86" size="text-lg" />
          <div className="flex items-center gap-6">
            <span className="overline text-white/50 hidden sm:inline">{admin.name || "Admin"}</span>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 overline hover:text-[#D9BE86] transition-colors">
              <LogOut size={16} strokeWidth={1.5} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1500px] mx-auto px-5 sm:px-8 py-12">
        <h1 className="font-serif font-light text-4xl sm:text-5xl tracking-tight">Atelier Dashboard</h1>

        <div className="grid grid-cols-3 gap-4 sm:gap-6 mt-10">
          {[
            ["Custom Orders", stats.orders, Package],
            ["New Orders", stats.new_orders, Package],
            ["Queries", stats.queries, MessageSquare],
          ].map(([label, val, Icon]) => (
            <div key={label} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`} className="bg-white border border-[#D5D0C5] rounded-sm p-6">
              <Icon size={20} strokeWidth={1.5} className="text-[#C6A15B]" />
              <p className="font-serif text-4xl sm:text-5xl font-light mt-4">{val}</p>
              <p className="overline text-[#7a7568] mt-2">{label}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mt-12 mb-8">
          {[["orders", `Custom Orders (${orders.length})`], ["queries", `Queries (${queries.length})`]].map(([id, label]) => (
            <button
              key={id}
              data-testid={`tab-${id}`}
              onClick={() => setTab(id)}
              className={`overline px-5 py-3 border transition-colors ${tab === id ? "bg-[#121212] text-[#F5F2EA] border-[#121212]" : "border-[#D5D0C5] text-[#7a7568] hover:border-[#121212]"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-[#7a7568] py-12">Loading submissions...</p>
        ) : tab === "orders" ? (
          <div data-testid="orders-list" className="space-y-4">
            {orders.length === 0 && <p className="text-[#7a7568] py-12">No custom orders yet.</p>}
            {orders.map((o) => (
              <div key={o.id} className="bg-white border border-[#D5D0C5] rounded-sm p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-serif text-2xl font-light">{o.name}</h3>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-[#7a7568]">
                      <a href={`mailto:${o.email}`} className="inline-flex items-center gap-1 link-underline"><Mail size={14} /> {o.email}</a>
                      {o.phone && <span className="inline-flex items-center gap-1"><Phone size={14} /> {o.phone}</span>}
                    </div>
                  </div>
                  <span className="overline text-[#7a7568]">{fmt(o.created_at)}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 border-t border-[#eee6d6] pt-5">
                  {[["Type", o.clothing_type], ["Occasion", o.occasion], ["Budget", o.budget], ["Colours", o.colors], ["Style", o.style], ["Measurements", o.measurements]].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k}><p className="overline text-[#7a7568]">{k}</p><p className="text-sm mt-1">{v}</p></div>
                  ))}
                </div>
                {o.requirements && <p className="text-sm text-[#4a463d] mt-4 leading-relaxed"><span className="overline text-[#7a7568]">Requirements</span><br />{o.requirements}</p>}
                {o.reference_link && <a href={o.reference_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 overline text-[#C6A15B] mt-3 link-underline">Reference <ArrowUpRight size={14} /></a>}
              </div>
            ))}
          </div>
        ) : (
          <div data-testid="queries-list" className="space-y-4">
            {queries.length === 0 && <p className="text-[#7a7568] py-12">No queries yet.</p>}
            {queries.map((q) => (
              <div key={q.id} className="bg-white border border-[#D5D0C5] rounded-sm p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-serif text-2xl font-light">{q.name}</h3>
                    <a href={`mailto:${q.email}`} className="inline-flex items-center gap-1 text-sm text-[#7a7568] mt-2 link-underline"><Mail size={14} /> {q.email}</a>
                  </div>
                  <span className="overline text-[#7a7568]">{fmt(q.created_at)}</span>
                </div>
                {q.subject && <p className="font-medium mt-4">{q.subject}</p>}
                <p className="text-sm text-[#4a463d] mt-2 leading-relaxed">{q.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
